import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";

// import { flat } from "@utils";
import { protectedRoutes } from "app/routes";

import GullLayout from "app/layouts/GullLayout";
import AccessDenied from "app/views/sessions/AccessDenied";
import { hasAgencyAccess, isAgencyRoute } from "app/utils/agencyAccess";
import { hasRequiredRole } from "app/auth/authRoles";
import useSessionSecurity from "app/hooks/useSessionSecurity";
import { setUserData } from "app/redux/auth/authSlice";
import localStorageService from "app/services/localStorageService";
import api from "app/utils/axiosInstance";

const userHasPermission = (pathname, user, routes) => {
  if (!user) {
    return false;
  }

  const matched = routes.find((r) => r.path === pathname);
  if (!matched) return true;

  if (!matched.auth || !matched.auth.length) return true;

  return hasRequiredRole(user, matched.auth);
};

export default function AuthGuard() {
  useSessionSecurity();
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const [previousRoute, setPreviousRoute] = useState(null);
  const [revalidatingAgency, setRevalidatingAgency] = useState(false);
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const agencyAllowed = !isAgencyRoute(pathname) || hasAgencyAccess(user);
  const hasPermission = userHasPermission(pathname, user, protectedRoutes) && (agencyAllowed || revalidatingAgency);

  useEffect(() => {
    if (previousRoute !== null) setPreviousRoute(pathname);
  }, [pathname, previousRoute]);

  useEffect(() => {
    // If user accesses an agency route but local Redux says no agency yet,
    // re-check with backend once to catch newly validated accountants without requiring re-login
    if (isAuthenticated && isAgencyRoute(pathname) && !hasAgencyAccess(user) && !revalidatingAgency) {
      setRevalidatingAgency(true);
      api
        .get("/users/me")
        .then((res) => {
          if (res?.data) {
            const freshUser = res.data;
            const stored = localStorageService.getItem("auth_user") || {};
            const updated = { ...stored, user: freshUser };
            localStorageService.setItem("auth_user", updated);
            dispatch(setUserData({ user: freshUser }));
          }
        })
        .catch((err) => {
          console.warn("Could not revalidate agency access:", err);
        })
        .finally(() => {
          setRevalidatingAgency(false);
        });
    }
  }, [pathname, isAuthenticated, user, revalidatingAgency, dispatch]);

  if (revalidatingAgency) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated && hasPermission ? (
        <GullLayout>
          <Outlet />
        </GullLayout>
      ) : (
        <>
          {isAuthenticated && !hasPermission ? (
            <AccessDenied />
          ) : (
            <Navigate to={previousRoute || "/sessions/signin"} replace={true} />
          )}
        </>
      )}
    </>
  );
}
